<!-- src/views/engineering/PlantEquipmentRegisterView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & INTEGRATED COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 relative z-30 text-left border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5"><WrenchScrewdriverIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Plant & Equipment Register</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Asset Intelligence • Dynamic Client Inventory Hub</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <router-link :to="{ name: 'SingleItemInspectionForm' }" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
                    <ClipboardDocumentCheckIcon class="h-4 w-4" /> <span>Single-Item Inspection (SS-PEI-1)</span>
                </router-link>

                <router-link :to="{ name: 'MultiItemInspectionForm' }" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2">
                    <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Multi-Item Check (SS-PEI-2)</span>
                </router-link>

                <router-link to="/engineering/projects" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
                  <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to Projects</span>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10">
              <div class="flex flex-wrap gap-4 items-center flex-grow">
                <div class="flex flex-col gap-1.5 min-w-[240px] flex-grow sm:flex-none">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Assets</span>
                  <input v-model="filters.search" type="text" placeholder="Asset ID or Description..." class="filter-input-dark" />
                </div>

                <div class="flex flex-col gap-1.5 min-w-[200px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status Filter</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <option value="In Service">In Service</option>
                    <option value="Quarantined">Quarantined</option>
                  </select>
                </div>
              </div>

              <button 
                @click="exportToExcel"
                :disabled="exporting || filteredAssets.length === 0"
                class="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-white/20 transition-all flex items-center gap-3 disabled:opacity-50 shadow-xl"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Register</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 2. METRICS SUMMARY ROW -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12 animate-fadeIn text-left">
          <div v-for="stat in assetStats" :key="stat.label" class="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex items-center gap-4">
             <div class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" :style="{ backgroundColor: stat.color + '15', color: stat.color }">
               <component :is="stat.icon" class="w-6 h-6" />
             </div>
             <div>
               <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">{{ stat.label }}</p>
               <h4 class="text-xl font-black text-[#0A1630] leading-none">{{ stat.value }}</h4>
             </div>
          </div>
        </div>

        <!-- 3. DATA DISPLAY -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hydrating Client Asset Inventory...</p>
        </div>

        <div v-else-if="filteredAssets.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Inventory Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No plant or equipment items configured. Add items via Admin Manage Options.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn text-left">
          <div 
            v-for="asset in paginatedAssets" 
            :key="asset.value"
            class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group"
          >
            <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
              
              <!-- ASSET ID & DESCRIPTION -->
              <div class="flex items-center gap-6 w-full xl:w-[35%] shrink-0 text-left">
                <div class="h-16 w-16 shrink-0 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center text-[#0A1630] font-black text-xs uppercase">
                  {{ asset.value }}
                </div>
                <div class="min-w-0">
                  <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight truncate leading-tight">
                    {{ asset.text }}
                  </h3>
                  <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest mt-1">
                    Client Managed Asset
                  </p>
                </div>
              </div>

              <!-- STATUS & ACTIONS -->
              <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                 <span class="px-4 py-1.5 inline-flex text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                   In Service
                 </span>

                 <router-link 
                  :to="{ name: 'SingleItemInspectionForm', params: { assetId: asset.value }}" 
                  class="px-6 py-3.5 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md flex items-center gap-2"
                >
                  <ClipboardDocumentCheckIcon class="h-4 w-4" /> <span>Inspect Asset</span>
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Symmetrical Pagination Controls -->
          <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
            <div class="flex items-center gap-4">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 transition-all cursor-pointer shadow-md border-none"
              >
                &larr; Prev
              </button>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
                Page {{ currentPage }} of {{ totalPages || 1 }}
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || totalPages === 0"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 transition-all cursor-pointer shadow-md border-none"
              >
                Next &rarr;
              </button>
            </div>
            
            <div class="flex items-center gap-3">
              <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
              <select 
                v-model="itemsPerPage" 
                class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
              >
                <option :value="25">25 Rows</option>
                <option :value="50">50 Rows</option>
                <option :value="100">100 Rows</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import { 
  WrenchScrewdriverIcon, ArrowUturnLeftIcon, TableCellsIcon, 
  MagnifyingGlassIcon, ClipboardDocumentCheckIcon, CheckCircleIcon,
  ShieldCheckIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const exporting = ref(false);
const assets = ref([]);

const currentPage = ref(1);
const itemsPerPage = ref(25);

const filters = reactive({
  search: '',
  status: 'all'
});

const fetchClientAssets = async () => {
  loading.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) {
      assets.value = [];
      loading.value = false;
      return;
    }
    const docRef = doc(db, 'formOptions', 'plantEquipmentItems');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data[cid] && Array.isArray(data[cid])) {
        assets.value = data[cid];
      } else if (data.global && Array.isArray(data.global)) {
        assets.value = data.global;
      } else {
        assets.value = [];
      }
    } else {
      assets.value = [];
    }
  } catch (err) {
    console.error("Failed to load client assets:", err);
    assets.value = [];
  } finally {
    loading.value = false;
  }
};

const assetStats = computed(() => {
  const total = assets.value.length;
  return [
    { label: 'Total Registered Assets', value: total, color: '#20FCF1', icon: WrenchScrewdriverIcon },
    { label: 'In Service', value: total, color: '#10B981', icon: CheckCircleIcon },
    { label: 'Compliance Status', value: 'Verified', color: '#FBBF24', icon: ShieldCheckIcon }
  ];
});

const filteredAssets = computed(() => {
  return assets.value.filter(a => {
    const queryStr = (filters.search || '').toLowerCase().trim();
    let matchSearch = true;
    if (queryStr) {
      const searchableText = [a.value, a.text].filter(Boolean).join(' ').toLowerCase();
      matchSearch = searchableText.includes(queryStr);
    }
    return matchSearch;
  });
});

const totalPages = computed(() => Math.ceil(filteredAssets.value.length / itemsPerPage.value));
const paginatedAssets = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredAssets.value.slice(start, start + itemsPerPage.value);
});

const prevPage = () => { if (currentPage.value > 1) currentPage.value--; };
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };

const exportToExcel = () => {
  exporting.value = true;
  try {
    const ws = XLSX.utils.json_to_sheet(filteredAssets.value);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Client_Equipment_Register");
    XLSX.writeFile(wb, "Sentinel_Plant_Equipment_Register.xlsx");
  } catch (err) { alert(err.message); } finally { exporting.value = false; }
};

onMounted(() => { 
  if (userStore.isAuthReady) fetchClientAssets();
  else {
    const unwatch = userStore.$subscribe(() => {
      if (userStore.isAuthReady) { fetchClientAssets(); unwatch(); }
    });
  }
});

watch([() => filters.search, () => filters.status, itemsPerPage], () => { currentPage.value =.1; });
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>